import React, { useRef } from "react";
import { X, Briefcase, Clock, AlertCircle, Plus, Upload, FileCheck, Paperclip } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";

const RequestModal = ({
  showRequestModal,
  setShowRequestModal,
  isPartialDayMode,
  setIsPartialDayMode,
  leaveForm,
  setLeaveForm,
  partialDayForm,
  setPartialDayForm,
  leaveTypes,
  leaveStats,
  isSubmitting,
  handleSubmitRequest,
  // ─── Prescription upload (sick leave only) ───
  prescription,
  isUploadingPrescription,
  prescriptionError,
  handlePrescriptionUpload,
  handleRemovePrescription,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  language,
  t,
}) => {
  const prescriptionInputRef = useRef(null);

  if (!showRequestModal) return null;

  const modalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    backdropFilter: "blur(10px)",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const modalContentStyle = {
    background: isDarkMode
      ? "rgba(15, 23, 42, 0.95)"
      : "rgba(248, 250, 252, 0.98)",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    border: isDarkMode
      ? "1px solid rgba(71, 84, 103, 0.3)"
      : "1px solid rgba(203, 213, 225, 0.2)",
    borderRadius:
      screenSize === "xs" ? "0rem" : screenSize === "sm" ? "1.25rem" : "1.5rem",
    padding: spacing.padding,
    width: screenSize === "xs" ? "100%" : "90%",
    maxWidth:
      screenSize === "xs" ? "100%" : screenSize === "sm" ? "35rem" : "43.75rem",
    margin: screenSize === "xs" ? "0" : "11.875rem auto",
    maxHeight: screenSize === "xs" ? "100vh" : "80vh",
    overflowY: "auto",
    direction: language === "ar" ? "rtl" : "ltr",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const inputStyle = {
    width: "100%",
    padding: screenSize === "xs" ? "12px 16px" : "16px 20px",
    borderRadius: screenSize === "xs" ? "10px" : "14px",
    border: isDarkMode
      ? "2px solid rgba(71,84,103,0.5)"
      : "2px solid rgba(203,213,225,0.3)",
    background: isDarkMode ? "rgba(15,23,42,0.8)" : "rgba(248,250,252,0.95)",
    color: isDarkMode ? "#e2e8f0" : "#1e293b",
    fontSize: screenSize === "xs" ? "14px" : "15px",
    fontWeight: "500",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    fontFamily: "'Cairo', sans-serif",
  };

  const labelStyle = {
    display: "block",
    fontSize: screenSize === "xs" ? "13px" : "14px",
    fontWeight: "700",
    color: isDarkMode ? "#ffffff" : "#1a1a1a",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "'Cairo', sans-serif",
  };

  // Sick leave can't be submitted until a prescription has been uploaded.
  const isSickLeave = !isPartialDayMode && leaveForm.type === "sick";
  const needsPrescription = isSickLeave && !prescription?.path;

  const isSubmitDisabled =
    isSubmitting ||
    isUploadingPrescription ||
    needsPrescription ||
    (isPartialDayMode
      ? !partialDayForm.date || !partialDayForm.halfDayType || !partialDayForm.reason
      : !leaveForm.type || !leaveForm.startDate || !leaveForm.endDate || !leaveForm.reason);

  return (
    <div style={modalStyle} onClick={() => setShowRequestModal(false)}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: screenSize === "xs" ? "12px" : "16px",
            marginBottom: screenSize === "xs" ? "24px" : "32px",
            paddingBottom: screenSize === "xs" ? "16px" : "24px",
            borderBottom: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: screenSize === "xs" ? "40px" : "48px",
              height: screenSize === "xs" ? "40px" : "48px",
              background: isPartialDayMode
                ? "linear-gradient(135deg, #8b5cf6, #8b5cf6)"
                : "linear-gradient(135deg, #2e7cba, #2e7cba)",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isPartialDayMode ? (
              <Clock size={screenSize === "xs" ? 20 : 24} color="#ffffff" />
            ) : (
              <Briefcase size={screenSize === "xs" ? 20 : 24} color="#ffffff" />
            )}
          </div>

          {/* Title */}
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: 0,
                color: isDarkMode ? "#ffffff" : "#1a1a1a",
                fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
                fontWeight: "700",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {isPartialDayMode ? t.leaveTypes.partialDay : t.request.title}
            </h2>
            <p
              style={{
                margin: "0.5rem 0 0 0",
                color: isDarkMode
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.6)",
                fontSize: screenSize === "xs" ? "0.875rem" : "1rem",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {isPartialDayMode
                ? t.leaveDescriptions.partialDay
                : t.pageSubtitle}
            </p>
          </div>

          {/* Close Button */}
          <button
            style={{
              background: isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
              border: "none",
              color: isDarkMode ? "white" : "#1a1a1a",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowRequestModal(false)}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* ===== PARTIAL DAY FORM ===== */}
        {isPartialDayMode ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: screenSize === "xs" ? "16px" : "24px" }}>
            {/* Date */}
            <div>
              <label style={labelStyle}>{t.request.date} *</label>
              <input
                type="date"
                value={partialDayForm.date}
                onChange={(e) =>
                  setPartialDayForm({ ...partialDayForm, date: e.target.value })
                }
                style={inputStyle}
              />
            </div>

            {/* Annual Leave Summary */}
            {(() => {
              const statTotal = leaveStats.find((s) => s.label === t.stats.totalAvailable)?.value;
              const statUsed = leaveStats.find((s) => s.label === t.stats.daysUsed)?.value;
              const statLeft = leaveStats.find((s) => s.label === t.stats.daysRemaining)?.value;
              const annualLeave = leaveTypes.find((lt) => lt.id === "annual") || { available: 0, used: 0 };
              const annTotal = Number(statTotal ?? annualLeave.available ?? 0);
              const annUsed = Number(statUsed ?? annualLeave.used ?? 0);
              const annLeft = Number(statLeft ?? Math.max(annTotal - annUsed, 0));
              const percentUsed = annTotal ? Math.round((annUsed / annTotal) * 100) : 0;

              const statCardStyle = {
                background: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
                padding: spacing.padding,
                borderRadius: "1rem",
                transition: "all 0.3s ease",
                boxShadow: isDarkMode
                  ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
                  : "0 0.5rem 2rem rgba(0,0,0,0.1)",
                backdropFilter: isDarkMode ? "blur(0.625rem)" : "blur(0px)",
                border: isDarkMode
                  ? "1px solid rgba(255,255,255,0.15)"
                  : "1px solid rgba(0,0,0,0.05)",
              };

              return (
                <div style={{ marginTop: "12px" }}>
                  <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                    {[
                      { label: t.balance.used, value: annUsed },
                      { label: t.balance.total, value: annTotal },
                      { label: t.balance.left, value: annLeft },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ ...statCardStyle, flex: 1, textAlign: language === "ar" ? "right" : "left" }}>
                        <div style={{ fontSize: fonts.small, color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)", marginBottom: "6px", fontFamily: "'Cairo', sans-serif" }}>{label}</div>
                        <div style={{ fontSize: screenSize === "xs" ? fonts.subtitle : fonts.title, fontWeight: 800 }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ height: "8px", background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(2,6,23,0.06)", borderRadius: "999px", overflow: "hidden", marginBottom: "6px" }}>
                    <div style={{ width: `${Math.min(Math.max(percentUsed, 0), 100)}%`, height: "100%", background: theme.colors.primary }} />
                  </div>
                  <div style={{ fontSize: fonts.small, color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.6)", fontFamily: "'Cairo', sans-serif" }}>
                    {percentUsed}% {t.stats.utilized}
                  </div>
                </div>
              );
            })()}

            {/* Half-Day Type */}
            <div>
              <label style={labelStyle}>{t.request.halfDayType} *</label>
              <select
                value={partialDayForm.halfDayType}
                onChange={(e) =>
                  setPartialDayForm({ ...partialDayForm, halfDayType: e.target.value })
                }
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="">{t.request.selectHalfDayType}</option>
                <option value="first_half">{t.request.firstHalf}</option>
                <option value="second_half">{t.request.secondHalf}</option>
              </select>
            </div>

            {/* Reason */}
            <div>
              <label style={labelStyle}>{t.request.reason} *</label>
              <textarea
                value={partialDayForm.reason}
                onChange={(e) =>
                  setPartialDayForm({ ...partialDayForm, reason: e.target.value })
                }
                placeholder={t.request.reasonPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>

            {/* Info Box */}
            <div
              style={{
                padding: screenSize === "xs" ? "12px 16px" : "16px 20px",
                background: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                borderRadius: "10px",
                color: "#8b5cf6",
                fontSize: screenSize === "xs" ? "13px" : "14px",
                fontFamily: "'Cairo', sans-serif",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <AlertCircle size={16} style={{ marginTop: "2px", flexShrink: 0 }} />
              <span>{t.leaveDescriptions.partialDay}</span>
            </div>
          </div>
        ) : (
          // ===== REGULAR LEAVE FORM =====
          <div
            style={{
              display: "grid",
              gridTemplateColumns: screenSize === "xs" ? "1fr" : "1fr 1fr",
              gap: screenSize === "xs" ? "16px" : "24px",
            }}
          >
            {/* Leave Type */}
            <div style={{ gridColumn: screenSize === "xs" ? "1" : "1 / -1" }}>
              <label style={labelStyle}>{t.request.leaveType} *</label>
              <ReusableDropdown
                items={leaveTypes}
                value={
                  leaveForm.type
                    ? (() => {
                        const found = leaveTypes.find((lt) => lt.id === leaveForm.type);
                        return found ? found.id : "";
                      })()
                    : ""
                }
                onChange={(val) => {
                  const selected = leaveTypes.find((lt) => lt.id === val);
                  if (selected) {
                    if (selected.id === "partialDay") {
                      setIsPartialDayMode(true);
                      setPartialDayForm({ date: new Date().toISOString().split("T")[0], halfDayType: "", reason: "" });
                      setLeaveForm({ ...leaveForm, type: "partialDay", startDate: "", endDate: "", reason: "", handover: "" });
                    } else {
                      setIsPartialDayMode(false);
                      setLeaveForm({ ...leaveForm, type: selected.id });
                    }
                  }
                }}
                onSelect={(item) => {
                  if (item.id === "partialDay") {
                    setIsPartialDayMode(true);
                    setPartialDayForm({ date: new Date().toISOString().split("T")[0], halfDayType: "", reason: "" });
                    setLeaveForm({ ...leaveForm, type: "partialDay", startDate: "", endDate: "", reason: "", handover: "" });
                  } else {
                    setIsPartialDayMode(false);
                    setLeaveForm({ ...leaveForm, type: item.id });
                  }
                }}
                placeholder={t.request.selectType}
                getItemKey={(item) => item.id}
                getItemLabel={(item) =>
                  `${item.name} (${Math.max(item.available - item.used, 0)} ${t.request.daysAvailable})`
                }
                renderItem={(item) => (
                  <div style={{ flex: 1, textAlign: language === "ar" ? "right" : "left", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ fontWeight: 600, color: isDarkMode ? "#f1f5f9" : "#334155", fontSize: "15px" }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: "12px", color: isDarkMode ? "rgba(203,213,225,0.6)" : "#6b7280" }}>
                      {Math.max(item.available - item.used, 0)} {t.request.daysAvailable}
                    </div>
                  </div>
                )}
                theme={isDarkMode ? "dark" : "light"}
                language={language}
                maxItems={10}
                showAllOnArrowClick={true}
                showClear={true}
                onClear={() => setLeaveForm({ ...leaveForm, type: "" })}
                inputStyle={{
                  fontWeight: "600",
                  fontSize: fonts.body,
                  background: isDarkMode ? "rgba(15,23,42,0.8)" : "rgba(248,250,252,0.95)",
                  border: isDarkMode ? "2px solid rgba(71,84,103,0.5)" : "2px solid rgba(203,213,225,0.3)",
                  color: isDarkMode ? "#e2e8f0" : "#1e293b",
                }}
              />
            </div>

            {/* Start Date */}
            <div>
              <label style={labelStyle}>{t.request.startDate} *</label>
              <input
                type="date"
                value={leaveForm.startDate}
                onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.target.value })}
                style={inputStyle}
              />
            </div>

            {/* End Date */}
            <div>
              <label style={labelStyle}>{t.request.endDate} *</label>
              <input
                type="date"
                value={leaveForm.endDate}
                onChange={(e) => setLeaveForm({ ...leaveForm, endDate: e.target.value })}
                min={leaveForm.startDate || undefined}
                style={inputStyle}
              />
            </div>

            {/* Reason */}
            <div>
              <label style={labelStyle}>{t.request.reason} *</label>
              <textarea
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder={t.request.reasonPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>

            {/* Handover */}
            <div>
              <label style={labelStyle}>{t.request.handover}</label>
              <textarea
                value={leaveForm.handover}
                onChange={(e) => setLeaveForm({ ...leaveForm, handover: e.target.value })}
                placeholder={t.request.handoverPlaceholder}
                rows={screenSize === "xs" ? 3 : 4}
                style={{ ...inputStyle, resize: "vertical", minHeight: screenSize === "xs" ? "100px" : "120px" }}
              />
            </div>

            {/* ===== PRESCRIPTION UPLOAD (sick leave only) ===== */}
            {isSickLeave && (
              <div style={{ gridColumn: screenSize === "xs" ? "1" : "1 / -1" }}>
                <label style={labelStyle}>{t.request.prescription} *</label>

                {/* Hidden native input — driven by the buttons below */}
                <input
                  ref={prescriptionInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handlePrescriptionUpload(file);
                    // Reset so picking the same file again still fires onChange
                    e.target.value = "";
                  }}
                />

                {prescription?.path ? (
                  // ── Uploaded state ──
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: screenSize === "xs" ? "12px 16px" : "16px 20px",
                      borderRadius: screenSize === "xs" ? "10px" : "14px",
                      background: "rgba(34, 197, 94, 0.1)",
                      border: "1px solid rgba(34, 197, 94, 0.35)",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    <FileCheck size={18} color="#22c55e" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: "#22c55e",
                          fontWeight: 700,
                          fontSize: screenSize === "xs" ? "13px" : "14px",
                        }}
                      >
                        {t.request.prescriptionUploaded}
                      </div>
                      <div
                        style={{
                          color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
                          fontSize: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {prescription.fileName}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePrescription}
                      disabled={isSubmitting}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#ef4444",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        fontFamily: "'Cairo', sans-serif",
                        flexShrink: 0,
                      }}
                    >
                      {t.request.removePrescription}
                    </button>
                  </div>
                ) : (
                  // ── Empty state ──
                  <button
                    type="button"
                    onClick={() => prescriptionInputRef.current?.click()}
                    disabled={isUploadingPrescription || isSubmitting}
                    style={{
                      ...inputStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      cursor: isUploadingPrescription ? "wait" : "pointer",
                      borderStyle: "dashed",
                      fontWeight: 600,
                    }}
                  >
                    {isUploadingPrescription ? (
                      <>
                        <Clock size={16} style={{ animation: "spin 1s linear infinite" }} />
                        {t.request.uploadingPrescription}
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        {t.request.uploadPrescription}
                      </>
                    )}
                  </button>
                )}

                {/* Hint / error */}
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "6px",
                    fontSize: "12px",
                    fontFamily: "'Cairo', sans-serif",
                    color: prescriptionError
                      ? "#ef4444"
                      : isDarkMode
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(26,26,26,0.6)",
                  }}
                >
                  {prescriptionError ? (
                    <AlertCircle size={14} style={{ marginTop: "1px", flexShrink: 0 }} />
                  ) : (
                    <Paperclip size={14} style={{ marginTop: "1px", flexShrink: 0 }} />
                  )}
                  <span>{prescriptionError || t.request.prescriptionHint}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation Errors */}
        {!isPartialDayMode && !leaveForm.type && leaveForm.reason && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.request.leaveType} {t.messages.selectDates}</span>
          </div>
        )}

        {!isPartialDayMode && leaveForm.startDate && leaveForm.endDate && new Date(leaveForm.startDate) > new Date(leaveForm.endDate) && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.messages.invalidDates}</span>
          </div>
        )}

        {needsPrescription && leaveForm.reason && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.request.prescriptionRequired}</span>
          </div>
        )}

        {isPartialDayMode && !partialDayForm.halfDayType && partialDayForm.reason && (
          <div style={{ marginTop: "20px", padding: "16px 20px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", color: "#ef4444", fontSize: screenSize === "xs" ? "13px" : "14px", fontFamily: "'Cairo', sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={16} />
            <span>{t.messages.selectHalfDayType}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: screenSize === "xs" ? "12px" : "16px",
            marginTop: "32px",
            paddingTop: "24px",
            borderTop: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            justifyContent: screenSize === "xs" ? "center" : "flex-end",
          }}
        >
          <button
            onClick={() => setShowRequestModal(false)}
            disabled={isSubmitting}
            style={{
              flex: screenSize === "xs" ? "1 1 48%" : "auto",
              padding: screenSize === "xs" ? "12px 16px" : "16px 24px",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              border: isDarkMode ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(0,0,0,0.1)",
              background: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
              color: isDarkMode ? "#ffffff" : "#1a1a1a",
              cursor: "pointer",
              fontSize: screenSize === "xs" ? "13px" : "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.request.cancel}
          </button>
          <button
            onClick={handleSubmitRequest}
            disabled={isSubmitDisabled}
            style={{
              flex: screenSize === "xs" ? "1 1 48%" : "auto",
              padding: screenSize === "xs" ? "12px 16px" : "16px 24px",
              borderRadius: screenSize === "xs" ? "10px" : "12px",
              border: "none",
              background: isSubmitDisabled
                ? "rgba(59, 130, 246, 0.5)"
                : "linear-gradient(135deg, #2e7cba, #2e7cba)",
              color: "#ffffff",
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              fontSize: screenSize === "xs" ? "13px" : "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {isSubmitting ? (
              <>
                <Clock size={16} style={{ animation: "spin 1s linear infinite" }} />
                {t.request.submitting}
              </>
            ) : (
              <>
                <Plus size={16} />
                {t.request.submitRequest}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestModal;